import { inject, Injectable } from '@angular/core';
import { IPaginatedItemsModel } from '@shared/sync/paginated-items.model';
import { ItemStockItem } from '../models/item-stock-item.model';
import { FilterBy } from '@shared/sync/filter-by.model';
import { SortBy } from '@shared/sync/sort-by.model';
import { GuidGeneratorService } from '@shared/tools/guid-generator.service';
import { HsBlazorService } from '@hsetu/core-services';

@Injectable({
  providedIn: 'root',
})
export class ItemStockService {
  private readonly _serviceKey = 'kfmItemStock';
  private readonly _blazorService = inject(HsBlazorService);
  private readonly _guidGenerator = inject(GuidGeneratorService);

  public async getAll(
    pageSize = 10,
    pageIndex = 0,
    sortBy: SortBy | null = null,
    filterBy: FilterBy | null = null,
  ): Promise<IPaginatedItemsModel<ItemStockItem>> {
    const stockResult = await this._blazorService.invokeService(
      this._serviceKey,
      'Get',
      {
        pageIndex: pageIndex,
        pageSize: pageSize,
        sortBy: sortBy,
        filterBy: filterBy,
      },
    );

    if (stockResult) {
      const result = stockResult as Record<string, unknown>;
      const items: IPaginatedItemsModel<ItemStockItem> = {
        count: Number(result['count']),
        data: result['data'] as ItemStockItem[],
        pageIndex: Number(result['pageIndex']),
        pageSize: Number(result['pageSize']),
      };

      return items;
    }

    return { count: 0, data: [], pageSize: 0, pageIndex: 0 };
  }

  public async getManufacturers(): Promise<string[]> {
    const manufacturersResponse = await this._blazorService.invokeService(
      this._serviceKey,
      'GetManufacturers',
    );

    if (manufacturersResponse) {
      const result = manufacturersResponse as Record<string, unknown>;
      return (result['manufacturers'] as string[]) || [];
    }

    return [];
  }

  public async getByIds(ids: string[]): Promise<ItemStockItem[]> {
    const itemsResult = await this._blazorService.invokeService(
      this._serviceKey,
      'GetByIds',
      { ids: ids },
    );

    if (itemsResult) {
      const result = itemsResult as Record<string, unknown>;
      return (result['data'] as ItemStockItem[]) || [];
    }

    return [];
  }

  public async createOrUpdate(
    item: ItemStockItem,
  ): Promise<ItemStockItem | undefined> {
    const id =
      item.uId === this._guidGenerator.emptyUUID ? undefined : item.uId;

    const request = {
      id: id,
      infoDesc: item.infoDesc,
      priceTotal: item.priceTotal,
      unit: item.unit,
      articleNumber: item.articleNumber,
      manufacturerLabel: item.manufacturerLabel,
      medium: undefined,
    };

    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Put',
      request,
    );

    if (result) {
      const id = (result as Record<string, unknown>)['itemStockId'] as string;
      item.uId = id;
      return item;
    }

    return undefined;
  }

  public async delete(id: string): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Delete',
      { id: id },
    );

    if (result) {
      const success = (result as Record<string, unknown>)[
        'isSuccess'
      ] as boolean;
      return success;
    }

    return false;
  }

  public async importEbCatalog(): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'ImportEBCatalog',
    );

    if (result) {
      const imported = (result as Record<string, unknown>)[
        'imported'
      ] as boolean;
      return imported;
    }

    return false;
  }
}
